import { AbstractControl } from '@angular/forms';

/**
 * Keys that hold UI-only runtime state on data models (e.g. PostForm.formGroup
 * and PostForm.model, attached in place when the preview's form viewer renders
 * a form add-on). They must never be serialized: a rendered FormGroup is
 * circular (each control's _parent points back at the group), and JSON.stringify
 * on a circular body throws BEFORE the HTTP request is created — the save fails
 * with no network call at all.
 */
const UI_RUNTIME_KEYS = new Set(['formGroup', 'model']);

/**
 * Deep-copies a value into a plain-data shape that is safe to JSON-serialize:
 * drops UI runtime keys and Angular form controls wherever they appear in the
 * graph, and breaks any object cycle by dropping the back-reference. Dates pass
 * through untouched so they still serialize to ISO strings.
 */
export function sanitizeForSerialization<T>(value: T): T {
  const ancestors = new WeakSet<object>();

  const strip = (val: any): any => {
    if (val === null || typeof val !== 'object') return val;
    if (val instanceof Date) return val;
    if (val instanceof AbstractControl) return undefined;
    if (ancestors.has(val)) return undefined; // cycle: drop the back-reference
    ancestors.add(val);
    try {
      if (Array.isArray(val)) {
        return val.map(strip);
      }
      const out: any = {};
      for (const [key, child] of Object.entries(val)) {
        if (UI_RUNTIME_KEYS.has(key)) continue;
        const stripped = strip(child);
        if (stripped !== undefined) out[key] = stripped;
      }
      return out;
    } finally {
      // Only ancestors on the current path count as cycles; the same object
      // referenced from two sibling branches is legitimate shared data.
      ancestors.delete(val);
    }
  };

  return strip(value);
}
